import { memo, useState } from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Grid, Button, Drawer } from '@mui/material'
import { Code, BugReport, Preview } from '@mui/icons-material'
import MappingView from '../mapping/MappingView'
import TestingView from '../testing/TestingView'
import MappingPreview from './MappingPreview'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'

export interface MappingDetailsProps {}

type ViewType = 'mapping' | 'testing'

const MappingDetails = memo(function MappingDetails(_props: MappingDetailsProps) {
  const { translationMap, mappingDetails } = useTranslationMapDetailsContext()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const { currentView, setCurrentView } = mappingDetails

  // Early return if context values are not available
  if (!translationMap) {
    return <div>Loading...</div>
  }

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: ViewType | null) => {
    if (newView !== null) {
      setCurrentView(newView)
      if (newView !== 'mapping') {
        setIsPreviewOpen(false)
      }
    }
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <ToggleButtonGroup
          value={currentView}
          exclusive
          onChange={handleViewChange}
          aria-label="view selection"
          size="small"
          sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1.5 } }}
        >
          <ToggleButton value="mapping" aria-label="mapping view">
            <Code sx={{ mr: 0.5, fontSize: '1rem' }} />
            Mapping
          </ToggleButton>
          <ToggleButton value="testing" aria-label="testing view">
            <BugReport sx={{ mr: 0.5, fontSize: '1rem' }} />
            Testing
          </ToggleButton>
        </ToggleButtonGroup>

        {currentView === 'mapping' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Preview />}
            onClick={() => setIsPreviewOpen(true)}
            sx={{
              py: 0.5,
              px: 1.5,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'divider',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Open preview
          </Button>
        )}
      </Box>

      {currentView === 'mapping' && (
        <Grid container spacing={0} sx={{ height: '100%', paddingBottom: 2 }}>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <MappingView />
          </Grid>
        </Grid>
      )}
      {currentView === 'testing' && <TestingView />}

      <Drawer
        anchor="right"
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: '50vw' },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
          <MappingPreview isEnabled={!!translationMap.encoded_implementation} />
        </Box>
      </Drawer>
    </Stack>
  )
})

export default MappingDetails
